// src/hooks/useLastVisited.js
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDataLayer } from '@quantiix/data-layer';
import { parseCurrentRoute } from '@/config/routes';
import { createLogger } from '@/utils/logger';

const logger = createLogger('Shell::useLastVisited');

/**
 * Route entity → session key the navbar reads when re-entering that MFE.
 *
 * Dedicated keys, not the shared `ouId`/`machineId` scope: the Machine MFE also
 * writes `ouId` (for its machine's OU), so reusing it would make the PublicView
 * icon follow the last machine instead of the last PublicView.
 */
const LAST_VISITED_KEYS = {
    publicview: 'lastPvOuId',
    machine: 'lastMachineId',
};

/**
 * Records the PublicView / Machine the user is currently on, so the navbar can
 * return them there.
 *
 * Both keys sit in the data-layer session's localStorage tier (see the package's
 * sessionPersistence VIEW_PREF_KEYS), so the memory survives closing the tab and
 * the next login — not just a refresh. They are pointers for the navbar, never the
 * active scope, and the navbar resolves them against the live hierarchy before
 * navigating, so an id that is gone or belongs to another company falls back.
 */
export function useLastVisited() {
    const { pathname } = useLocation();
    const { setScope } = useDataLayer();

    useEffect(() => {
        const { entityType, entityId } = parseCurrentRoute(pathname);
        const key = LAST_VISITED_KEYS[entityType];

        if (!key || !entityId) return;

        logger.info('Recording last visited', { key, entityId });
        setScope({ [key]: entityId });
    }, [pathname, setScope]);
}

export default useLastVisited;
